import { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';

export const AICore = ({ 
    state = 'idle', // idle, listening, thinking, speaking
    size = 'lg',
    className 
}) => {
    const [bars, setBars] = useState(Array(12).fill(0.3));

    useEffect(() => {
        if (state === 'listening' || state === 'speaking') {
            const interval = setInterval(() => {
                setBars(prev => prev.map(() => 
                    0.2 + Math.random() * 0.8
                ));
            }, 100);
            return () => clearInterval(interval);
        } else if (state === 'thinking') {
            const interval = setInterval(() => {
                setBars(prev => prev.map((_, i) => 
                    0.3 + Math.sin(Date.now() / 200 + i * 0.5) * 0.3
                ));
            }, 50);
            return () => clearInterval(interval);
        } else {
            setBars(Array(12).fill(0.3));
        }
    }, [state]);

    const sizeClasses = {
        sm: 'w-24 h-24',
        md: 'w-32 h-32',
        lg: 'w-48 h-48',
        xl: 'w-64 h-64',
    };

    const stateColors = {
        idle: 'from-indigo-500/60 to-sky-500/40',
        listening: 'from-sky-500 to-indigo-500',
        thinking: 'from-indigo-500 to-purple-500',
        speaking: 'from-emerald-500 to-sky-500',
    };

    return (
        <div className={cn('relative flex items-center justify-center', className)}>
            {/* Outer glow ring */}
            <div className={cn(
                'absolute rounded-full bg-gradient-to-br opacity-20 blur-xl transition-all duration-500',
                sizeClasses[size],
                stateColors[state],
                state !== 'idle' && 'animate-pulse-glow'
            )} />

            {/* Main orb */}
            <div className={cn(
                'relative rounded-full bg-gradient-to-br flex items-center justify-center transition-all duration-300',
                sizeClasses[size],
                stateColors[state],
                state === 'thinking' && 'animate-spin-slow'
            )}>
                {/* Inner visualization */}
                <div className="flex items-end gap-1 h-1/2">
                    {bars.map((height, i) => (
                        <div
                            key={i}
                            className="w-1.5 bg-white/80 rounded-full transition-all duration-100"
                            style={{ 
                                height: `${height * 100}%`,
                                animationDelay: `${i * 50}ms`
                            }}
                        />
                    ))}
                </div>

                {/* Rotating border */}
                <div className={cn(
                    'absolute inset-0 rounded-full border-2 border-white/20',
                    state !== 'idle' && 'animate-spin-slow'
                )} />
            </div>

            {/* State label */}
            <div className="absolute -bottom-8 left-1/2 -translate-x-1/2">
                <span className={cn(
                    'text-xs uppercase tracking-wider font-medium px-3 py-1 rounded-full',
                    state === 'idle' && 'text-muted-foreground bg-muted',
                    state === 'listening' && 'text-sky-500 bg-sky-500/10',
                    state === 'thinking' && 'text-indigo-500 bg-indigo-500/10',
                    state === 'speaking' && 'text-emerald-500 bg-emerald-500/10',
                )}>
                    {state === 'idle' && 'Ready'}
                    {state === 'listening' && 'Listening...'}
                    {state === 'thinking' && 'Processing...'}
                    {state === 'speaking' && 'Speaking...'}
                </span>
            </div>
        </div>
    );
};
